/**
 * Kotlin generator suite - Composed generator with dependency injection
 * Provides a unified functional interface for all Kotlin code generation
 *
 * This suite provides a fully functional architecture with dependency injection:
 * - Type mappers: Functional (DI ready)
 * - Code emitters: Functional (DI ready)
 * - Protocol generators: Functional (DI ready)
 * - Struct generators: Functional (DI ready)
 * - Method generators: Functional (DI ready)
 * - Bridge generators: Functional (DI ready)
 *
 * This approach provides:
 * ✅ Clean functional API for users
 * ✅ Full dependency injection throughout the stack
 * ✅ Easy testing and customization
 * ✅ Composable and predictable code generation
 */

import type { ParsedModule } from "../../parser";
import type {
  KotlinCodeEmitter as KotlinCodeEmitterInterface,
  KotlinCodeEmitterOptions,
} from "./kotlin-code-emitter";
import type {
  KotlinTypeMapper as KotlinTypeMapperInterface,
  KotlinTypeMappingOptions,
} from "./kotlin-type-mapper";
import { createKotlinCodeEmitter } from "./kotlin-code-emitter";
import { createKotlinTypeMapper } from "./kotlin-type-mapper";

// Import functional generators
import type { KotlinProtocolGenerator as KotlinProtocolGeneratorInterface } from "./kotlin-protocol-generator";
import type { KotlinStructGenerator as KotlinStructGeneratorInterface } from "./kotlin-struct-generator";
import type { KotlinMethodGenerator as KotlinMethodGeneratorInterface } from "./kotlin-method-generator";
import type { KotlinBridgeGenerator as KotlinBridgeGeneratorInterface } from "./kotlin-bridge-generator";
import { createKotlinProtocolGenerator } from "./kotlin-protocol-generator";
import { createKotlinStructGenerator } from "./kotlin-struct-generator";
import { createKotlinMethodGenerator } from "./kotlin-method-generator";
import { createKotlinBridgeGenerator } from "./kotlin-bridge-generator";

export interface KotlinGeneratorSuiteOptions {
  /**
   * Type mapping options
   */
  typeMappingOptions?: KotlinTypeMappingOptions;
  /**
   * Code emitter options
   */
  codeEmitterOptions?: KotlinCodeEmitterOptions;
}

export interface KotlinGeneratorSuiteDeps {
  typeMapper: KotlinTypeMapperInterface;
  codeEmitter: KotlinCodeEmitterInterface;
  protocolGenerator: KotlinProtocolGeneratorInterface;
  structGenerator: KotlinStructGeneratorInterface;
  methodGenerator: KotlinMethodGeneratorInterface;
  bridgeGenerator: KotlinBridgeGeneratorInterface;
}

export interface KotlinGeneratorSuite {
  /**
   * Dependencies used by this suite
   */
  deps: KotlinGeneratorSuiteDeps;

  /**
   * Generate interface/spec for a module
   */
  generateInterface(module: ParsedModule): string;

  /**
   * Generate data class types for a module
   */
  generateDataClasses(module: ParsedModule): string;

  /**
   * Generate bridge implementation files
   */
  generateBridge(modules: ParsedModule[]): {
    bridgeModule: string;
    bridgeImplementation: string;
    buildGradle: string;
  };

  /**
   * Generate complete Kotlin implementation for a module
   */
  generateKotlinImplementation(module: ParsedModule): string;

  /**
   * Generate library mode interface (public API)
   */
  generateLibraryInterface(module: ParsedModule): string;

  /**
   * Generate library mode data classes
   */
  generateLibraryDataClasses(module: ParsedModule): string;
}

/**
 * Creates a Kotlin generator suite with composed dependencies
 */
export function createKotlinGeneratorSuite(
  options: KotlinGeneratorSuiteOptions = {},
): KotlinGeneratorSuite {
  const { typeMappingOptions = {}, codeEmitterOptions = {} } = options;

  // Create base dependencies
  const typeMapper = createKotlinTypeMapper(typeMappingOptions);
  const codeEmitter = createKotlinCodeEmitter(codeEmitterOptions);

  // Create functional generators with dependencies
  const protocolGenerator = createKotlinProtocolGenerator({ typeMapper, codeEmitter });
  const structGenerator = createKotlinStructGenerator({ typeMapper, codeEmitter });
  const methodGenerator = createKotlinMethodGenerator({ typeMapper });
  const bridgeGenerator = createKotlinBridgeGenerator({ codeEmitter, methodGenerator });

  const deps: KotlinGeneratorSuiteDeps = {
    typeMapper,
    codeEmitter,
    protocolGenerator,
    structGenerator,
    methodGenerator,
    bridgeGenerator,
  };

  /**
   * Generate interface/spec for a module
   */
  function generateInterface(module: ParsedModule): string {
    return protocolGenerator.generateLibraryModuleInterface(module);
  }

  /**
   * Generate data class types for a module
   */
  function generateDataClasses(module: ParsedModule): string {
    return structGenerator.generateModuleTypes(module);
  }

  /**
   * Generate bridge implementation files
   */
  function generateBridge(_modules: ParsedModule[]): {
    bridgeModule: string;
    bridgeImplementation: string;
    buildGradle: string;
  } {
    return {
      bridgeModule: "// Use AndroidPlatform.generateFiles() for full bridge generation",
      bridgeImplementation:
        "// Use AndroidPlatform.generateFiles() for full bridge generation",
      buildGradle: "// Use AndroidPlatform.generateFiles() for full bridge generation",
    };
  }

  /**
   * Generate complete Kotlin implementation for a module
   */
  function generateKotlinImplementation(_module: ParsedModule): string {
    return "// Use AndroidPlatform.generateFiles() for full implementation";
  }

  /**
   * Generate library mode interface (public API)
   */
  function generateLibraryInterface(module: ParsedModule): string {
    return protocolGenerator.generateLibraryModuleInterface(module);
  }

  /**
   * Generate library mode data classes
   */
  function generateLibraryDataClasses(module: ParsedModule): string {
    return structGenerator.generateModuleTypes(module);
  }

  return {
    deps,
    generateInterface,
    generateDataClasses,
    generateBridge,
    generateKotlinImplementation,
    generateLibraryInterface,
    generateLibraryDataClasses,
  };
}

/**
 * Helper to create a Kotlin generator suite with module-specific options
 */
export function createKotlinGeneratorForModule(
  module: ParsedModule,
): KotlinGeneratorSuite {
  return createKotlinGeneratorSuite({
    typeMappingOptions: {
      moduleName: module.moduleName,
    },
  });
}
